import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type CategoryNode = {
  id: string;
  parentId: string | null;
  externalCode: string | null;
  name: string;
  slug: string;
  sortOrder: number;
  children: CategoryNode[];
};

export type CategoryRow = {
  id: string;
  parentId: string | null;
  externalCode: string | null;
  name: string;
  slug: string;
  depth: number;
  path: string;
  hasChildren: boolean;
  /** Root-to-parent ids, used to hide a whole collapsed subtree in one pass. */
  ancestorIds: string[];
};

const KEY = ['catalog', 'categories'] as const;

export function useCategories() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<CategoryNode[]> => {
      const res = await apiClient.get<CategoryNode[]>('/catalog/categories');
      return res.data;
    },
  });
}

export function useCategoriesFlat() {
  const q = useCategories();
  const flat = q.data === undefined ? [] : flatten(q.data);
  return { ...q, flat };
}

function flatten(
  nodes: CategoryNode[],
  depth = 0,
  prefix = '',
  ancestorIds: string[] = [],
): CategoryRow[] {
  const out: CategoryRow[] = [];
  for (const n of nodes) {
    const path = prefix === '' ? n.name : `${prefix} / ${n.name}`;
    out.push({
      id: n.id,
      parentId: n.parentId,
      externalCode: n.externalCode,
      name: n.name,
      slug: n.slug,
      depth,
      path,
      hasChildren: n.children.length > 0,
      ancestorIds,
    });
    out.push(...flatten(n.children, depth + 1, path, [...ancestorIds, n.id]));
  }
  return out;
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; slug: string; parentId: string | null }) => {
      const res = await apiClient.post('/catalog/categories', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      patch: Partial<{ name: string; slug: string; parentId: string | null; sortOrder: number }>;
    }) => {
      const res = await apiClient.patch(`/catalog/categories/${input.id}`, input.patch);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/catalog/categories/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
